/**
 * Motion dice: reading the phone's motion sensor (`devicemotion`). iPhone browsers ask for
 * permission, and only from a tap (`requestMotion`, called from the Roll button's click);
 * Android browsers don't ask. Desktop browsers either never fire the event or fire it with no
 * values, so `MotionTracker.live` stays false there and the tray falls back to a swipe.
 */
import { ShakeDetector, type ShakeEvent } from './shake';
import type { MotionSample } from './toss';

type PermissionFn = () => Promise<'granted' | 'denied'>;
type MotionEventCtor = typeof DeviceMotionEvent & { requestPermission?: PermissionFn };

let permission: 'unknown' | 'granted' | 'denied' = 'unknown';

/**
 * Ask for motion access where the browser needs it. Must run inside a click handler. Resolves
 * false when there is no sensor API or permission is denied (then the tray is swipe-only).
 */
export async function requestMotion(): Promise<boolean> {
  if (typeof window === 'undefined' || typeof DeviceMotionEvent === 'undefined') return false;
  const ctor = DeviceMotionEvent as MotionEventCtor;
  if (typeof ctor.requestPermission !== 'function') return true;
  if (permission !== 'unknown') return permission === 'granted';
  try {
    permission = (await ctor.requestPermission()) === 'granted' ? 'granted' : 'denied';
  } catch {
    // Not from a tap, or blocked: try again on the next tap.
    return false;
  }
  return permission === 'granted';
}

/** How long samples are kept. */
const KEEP_MS = 1200;
/** Low-pass weight for the gravity estimate (devices that only give acceleration with gravity). */
const GRAVITY_ALPHA = 0.85;
/** The same where the browser gives gravity cleanly (with gravity minus without): just steadied. */
const GRAVITY_ALPHA_CLEAN = 0.5;

/** Collects motion samples while the tray is open, plus a smoothed shake level for the dice in your hand. */
export class MotionTracker {
  samples: MotionSample[] = [];
  /** True once the sensor has given a real reading. */
  live = false;
  /** How hard the phone is moving right now, m/s² smoothed (the dice rattle with it). */
  energy = 0;
  /** The last reading's acceleration across the screen (x right, y up the screen), for the rattle's direction. */
  lastPlanar = { x: 0, y: 0 };
  /** Shaking without holding the screen (`shake.ts`): its swings and when the dice are let go. */
  readonly shake = new ShakeDetector();
  private onShake: ((e: ShakeEvent) => void) | null = null;
  private gravity: [number, number, number] | null = null;
  private onMotion = (e: DeviceMotionEvent) => this.add(e);

  /** Start listening; `onShake` hears each swing of a shake and the release (`shake.ts`). */
  start(onShake?: (e: ShakeEvent) => void): void {
    this.onShake = onShake ?? null;
    this.samples = [];
    this.gravity = null;
    this.energy = 0;
    this.shake.reset();
    window.addEventListener('devicemotion', this.onMotion);
  }

  stop(): void {
    this.onShake = null;
    window.removeEventListener('devicemotion', this.onMotion);
  }

  /** Readings since `from` (ms, `performance.now()` clock). */
  since(from: number): MotionSample[] {
    return this.samples.filter((s) => s.t >= from);
  }

  private add(e: DeviceMotionEvent): void {
    let ax: number;
    let ay: number;
    let az: number;
    const lin = e.acceleration;
    const g = e.accelerationIncludingGravity;
    const hasLin = !!lin && lin.x !== null && lin.y !== null && lin.z !== null;
    const hasG = !!g && g.x !== null && g.y !== null && g.z !== null;
    if (!hasLin && !hasG) return;
    if (hasG) {
      // Gravity (which way is up, for lobs and drives): with gravity minus without, or smoothed.
      const raw: [number, number, number] = hasLin ? [g.x! - lin.x!, g.y! - lin.y!, g.z! - lin.z!] : [g.x!, g.y!, g.z!];
      const grav = (this.gravity ??= [...raw]);
      const k = hasLin ? GRAVITY_ALPHA_CLEAN : GRAVITY_ALPHA;
      for (let i = 0; i < 3; i++) grav[i] = k * grav[i]! + (1 - k) * raw[i]!;
    }
    if (hasLin) {
      ax = lin.x!;
      ay = lin.y!;
      az = lin.z!;
    } else {
      const grav = this.gravity!;
      [ax, ay, az] = [g!.x! - grav[0], g!.y! - grav[1], g!.z! - grav[2]];
    }
    const grav = this.gravity;
    const glen = grav ? Math.hypot(...grav) : 0;
    const up = grav && glen > 3 ? { ux: grav[0] / glen, uy: grav[1] / glen, uz: grav[2] / glen } : {};
    const r = e.rotationRate;
    const t = performance.now();
    if (!this.live && (ax !== 0 || ay !== 0 || az !== 0)) this.live = true;
    this.samples.push({ t, ax, ay, az, rx: r?.beta ?? 0, ry: r?.gamma ?? 0, rz: r?.alpha ?? 0, ...up });
    while (this.samples.length > 0 && this.samples[0]!.t < t - KEEP_MS) this.samples.shift();
    this.energy = this.energy * 0.8 + Math.hypot(ax, ay, az) * 0.2;
    this.lastPlanar = { x: ax, y: ay };
    const ev = this.shake.push(this.samples[this.samples.length - 1]!);
    if (ev) this.onShake?.(ev);
  }
}
